import { Sun, Cloud, CloudRain, CloudLightning, Moon } from "@phosphor-icons/react";
import { motion } from "framer-motion";

/**
 * WeatherScene — soft, friendly SVG illustrations keyed off the current theme.
 * Scales to 200-320px and animates subtly (floating clouds, drifting sun).
 *
 * Kept as CSS-gradient discs + Phosphor duotone icons instead of heavy SVGs
 * so it renders instantly and matches the pastel palette exactly.
 */
export default function WeatherScene({ theme, size = 220 }) {
  const { illustration, accent } = theme;

  if (illustration === "sun") {
    return (
      <div
        className="relative"
        style={{ width: size, height: size }}
        data-testid="weather-scene-sun"
      >
        {/* Sun disc with warm glow */}
        <motion.div
          className="absolute rounded-full"
          style={{
            inset: "14%",
            background: "radial-gradient(circle at 30% 30%, #fcd34d 0%, #f59e0b 60%, #d97706 100%)",
            boxShadow: "0 20px 60px rgba(245,158,11,0.35)",
          }}
          animate={{ y: [-4, 4, -4] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
        {/* Outer ring (halo) */}
        <div
          className="absolute rounded-full"
          style={{
            inset: 0,
            background: "radial-gradient(circle, rgba(252,211,77,0.30) 0%, transparent 70%)",
          }}
        />
      </div>
    );
  }

  if (illustration === "clouds") {
    return (
      <div
        className="relative"
        style={{ width: size, height: size }}
        data-testid="weather-scene-clouds"
      >
        <motion.div
          animate={{ x: [-6, 6, -6] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute inset-0 flex items-center justify-center"
        >
          <Cloud size={size * 0.9} weight="fill" color="#cbd5e1" />
        </motion.div>
        <motion.div
          animate={{ x: [4, -4, 4] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
          className="absolute"
          style={{ left: "45%", top: "18%" }}
        >
          <Cloud size={size * 0.55} weight="fill" color="#e2e8f0" />
        </motion.div>
      </div>
    );
  }

  if (illustration === "rain") {
    return (
      <div
        className="relative"
        style={{ width: size, height: size }}
        data-testid="weather-scene-rain"
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <CloudRain size={size * 0.92} weight="duotone" color={accent} />
        </div>
        {/* animated raindrops */}
        {[0, 1, 2, 3].map((i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              width: 4,
              height: 12,
              background: accent,
              left: `${35 + i * 10}%`,
              top: "62%",
              opacity: 0.7,
            }}
            animate={{ y: [0, 40], opacity: [0.8, 0] }}
            transition={{
              duration: 1.1,
              repeat: Infinity,
              delay: i * 0.22,
              ease: "easeIn",
            }}
          />
        ))}
      </div>
    );
  }

  if (illustration === "storm") {
    return (
      <div
        className="relative"
        style={{ width: size, height: size }}
        data-testid="weather-scene-storm"
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <CloudLightning size={size * 0.95} weight="duotone" color={accent} />
        </div>
        <motion.div
          className="absolute inset-0"
          animate={{ opacity: [0, 0.9, 0, 0] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "linear" }}
          style={{
            background: `radial-gradient(circle at 50% 60%, ${accent} 0%, transparent 50%)`,
            filter: "blur(12px)",
          }}
        />
      </div>
    );
  }

  // night
  return (
    <div
      className="relative"
      style={{ width: size, height: size }}
      data-testid="weather-scene-night"
    >
      <motion.div
        className="absolute inset-0 flex items-center justify-center"
        animate={{ rotate: [-3, 3, -3] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      >
        <Moon size={size * 0.75} weight="duotone" color={accent} />
      </motion.div>
      {/* faint stars */}
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="absolute rounded-full bg-white"
          style={{
            width: 3,
            height: 3,
            left: `${20 + i * 30}%`,
            top: `${25 + (i % 2) * 20}%`,
            boxShadow: "0 0 6px rgba(255,255,255,0.8)",
          }}
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 2 + i, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}

/** Tiny helper for timeline icons — matches scene type */
export function TimelineIcon({ code, size = 22, theme }) {
  const c = theme?.textSecondary || "#64748b";
  const accent = theme?.accent || "#f59e0b";
  if (code == null) return null;
  if (code >= 95) return <CloudLightning size={size} weight="fill" color={accent} />;
  if (code >= 80) return <CloudRain size={size} weight="fill" color={accent} />;
  if (code >= 51) return <CloudRain size={size} weight="duotone" color={c} />;
  if (code >= 45) return <Cloud size={size} weight="duotone" color={c} />;
  if (code >= 3) return <Cloud size={size} weight="duotone" color={c} />;
  if (code <= 2) return <Sun size={size} weight="fill" color={accent} />;
  return <Cloud size={size} weight="duotone" color={c} />;
}
